// The ONLY file in the repo allowed to import @statsig/* (enforced by the eslint fence in package.json).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  StatsigProvider,
  useClientAsyncInit,
  useStatsigClient,
} from "@statsig/react-bindings";
import { registerChecker } from "../activeFlags";
import { FeatureFlagContext } from "../context";
import { defaultFor } from "../registry";

const STATSIG_TIERS = {
  local: "development",
  staging: "staging",
  production: "production",
};

export const tierFor = (environment) => STATSIG_TIERS[environment];

const REFRESH_INTERVAL_MS = 5 * 60 * 1000;

// Stable reference so a caller that doesn't pass `refreshOn` doesn't churn
// the effect below on every render.
const NO_REFRESH_EVENTS = [];

// Sits inside StatsigProvider so values_updated re-renders it; bridges the
// native context into our vendor-free FeatureFlagContext.
const FlagBridge = ({ isLoading, isReady, children }) => {
  const { checkGate } = useStatsigClient();

  const isFeatureActive = useCallback(
    (flagName) => {
      try {
        return isLoading ? defaultFor(flagName) : checkGate(flagName);
      } catch {
        return defaultFor(flagName);
      }
    },
    [checkGate, isLoading],
  );

  useEffect(() => {
    registerChecker(isFeatureActive);
    return () => registerChecker(null);
  }, [isFeatureActive]);

  const value = useMemo(
    () => ({ isFeatureActive, isReady }),
    [isFeatureActive, isReady],
  );

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export const StatsigFlagProvider = ({
  sdkKey,
  environment,
  user,
  refreshOn = NO_REFRESH_EVENTS,
  children,
}) => {
  const { client, isLoading } = useClientAsyncInit(sdkKey, user(), {
    environment: { tier: tierFor(environment) },
  });
  // True while the client fetches values for a DIFFERENT person (an in-app login
  // or profile switch): until then it still answers for the previous identity,
  // so flags aren't ready for this one. Same-person refreshes (focus, interval)
  // don't count — they would flash every flag-gated screen back to a loader.
  const [isSwitchingUser, setIsSwitchingUser] = useState(false);

  useEffect(() => {
    let isMounted = true;
    let latestSwitch = 0;
    const refresh = () => {
      const nextUser = user();
      // The handle reads the client's current user without bumping its session.
      const isNewIdentity =
        client.getContextHandle().user?.userID !== nextUser.userID;
      const switchId = isNewIdentity ? (latestSwitch += 1) : null;
      if (isNewIdentity) setIsSwitchingUser(true);
      client
        .updateUserAsync(nextUser)
        .catch(() => {})
        .then(() => {
          if (isMounted && switchId !== null && switchId === latestSwitch) {
            setIsSwitchingUser(false);
          }
        });
    };
    // The bindings reuse one client per sdkKey across remounts; re-sync the
    // identity in case a different user signed in since the client was built.
    if (client.loadingStatus === "Ready") refresh();
    window.addEventListener("focus", refresh);
    // `focus` and the interval below catch a stale identity eventually, but
    // an in-app login/profile-switch neither reloads nor blurs the window —
    // it only fires the app-specific events named in `refreshOn` (see
    // index.js). Without this, a freshly logged-in user keeps evaluating as
    // the previous identity (often "anonymous") for up to REFRESH_INTERVAL_MS,
    // which can put them on the wrong side of a percentage ramp.
    refreshOn.forEach((eventName) => window.addEventListener(eventName, refresh));
    const intervalId = setInterval(refresh, REFRESH_INTERVAL_MS);
    return () => {
      isMounted = false;
      window.removeEventListener("focus", refresh);
      refreshOn.forEach((eventName) => window.removeEventListener(eventName, refresh));
      clearInterval(intervalId);
    };
  }, [client, user, refreshOn]);

  // On a repeat visit the client starts with this person's cached values and only
  // then fetches fresh ones. Answer from the cache meanwhile: waiting for the
  // network would hold every flag-gated screen (e.g. /home) on a loader for a
  // round trip on every page load. A first visit has no cache and still waits.
  const hasCachedValues = isLoading && !!client.getContextHandle().values;
  const isWaitingForValues = isLoading && !hasCachedValues;

  return (
    <StatsigProvider client={client}>
      <FlagBridge
        isLoading={isWaitingForValues}
        isReady={!isWaitingForValues && !isSwitchingUser}
      >
        {children}
      </FlagBridge>
    </StatsigProvider>
  );
};
